import { DotsThreeVertical, PencilSimple, Trash } from '@phosphor-icons/react';
import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ClipLoader } from 'react-spinners';
import { VendorBusinessLocation } from '../../../../../../types/dbTypes';
import { useScreenView } from '../../../../../../utils/ScreenViewContext';

function BusinessLocationsTable({
  locations,
  loading,
}: {
  locations: VendorBusinessLocation[];
  loading: boolean;
}) {
  const { screenView } = useScreenView();
  const navigate = useNavigate();
  const [popupVisibleMap, setPopupVisibleMap] = useState<Map<string, boolean>>(
    new Map()
  );
  const popupRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const togglePopup = (index: string | undefined) => {
    if (!index) return;
    setPopupVisibleMap((prevMap) => {
      const newMap = new Map(prevMap);
      newMap.set(index, !newMap.get(index)); // Toggle visibility for the item at index
      return newMap;
    });
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      let shouldClose = false;
      popupVisibleMap.forEach((isVisible, key) => {
        if (
          isVisible &&
          popupRefs.current[key] &&
          !popupRefs.current[key]?.contains(event.target as Node)
        ) {
          shouldClose = true;
        }
      });

      if (shouldClose) {
        setPopupVisibleMap(new Map()); // Close all popups if clicked outside
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [popupVisibleMap]);
  const renderScreenView = (screenView: string) => {
    switch (screenView) {
      case 'desktop':
      case 'tablet':
      case 'mobile':
        return (
          <div className="flex h-full w-full flex-col items-center overflow-x-hidden overflow-y-hidden">
            {!loading ? (
              <div className="min-h-[80vh] w-full overflow-x-auto">
                <table className="min-w-full table-auto rounded-lg bg-white">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-4 py-2 text-left text-[14px] font-[600] leading-[18px] text-primary">
                        Location
                      </th>
                      <th className="px-4 py-2 text-left text-[14px] font-[600] leading-[18px] text-primary">
                        Phone Number
                      </th>
                      <th className="px-4 py-2 text-left text-[14px] font-[600] leading-[18px] text-primary">
                        Website URL
                      </th>
                      <th className="px-4 py-2 text-left text-[14px] font-[600] leading-[18px] text-primary">
                        Email
                      </th>
                      <th className="px-4 py-2 text-left text-[14px] font-[600] leading-[18px] text-primary"></th>
                    </tr>
                  </thead>
                  <tbody className="overflow-y-auto">
                    {locations.map((loc, index) => (
                      <tr
                        key={index}
                        className="h-[65px] max-h-[65px] border-b border-gray-200"
                      >
                        <td className="h-[65px] max-h-[65px] px-4 py-2 text-[14px] text-primary">
                          {loc?.address_st_number +
                            ' ' +
                            loc?.address_st_name +
                            (loc?.address_unit ? ' ' + loc?.address_unit : '') +
                            ', ' +
                            loc?.address_city +
                            ', ' +
                            loc?.address_state +
                            ' ' +
                            loc?.address_zip}
                        </td>
                        <td className="h-[65px] max-h-[65px] px-4 py-2 text-[14px] text-primary">
                          {loc?.business_phone_number}
                        </td>
                        <td className="h-[65px] max-h-[65px] px-4 py-2 text-[14px] text-primary">
                          {loc?.website_url}
                        </td>
                        <td className="h-[65px] max-h-[65px] px-4 py-2 text-[14px] text-primary">
                          {loc?.email}
                        </td>
                        <td>
                          <button
                            onClick={(e: any) => {
                              e.stopPropagation();
                              togglePopup(loc?.location_id ?? '');
                            }}
                            className="relative"
                          >
                            <DotsThreeVertical size={26} weight="bold" />
                            {popupVisibleMap.get(loc?.location_id ?? '') && (
                              <div
                                ref={(el) => {
                                  popupRefs.current[loc?.location_id ?? ''] =
                                    el;
                                }}
                                className="absolute right-0 top-[1.5rem] z-40 w-44 rounded-lg border-[1px] border-bordergray bg-white shadow-lg drop-shadow-xl"
                              >
                                <div>
                                  <button
                                    onClick={(e: any) => {
                                      e.stopPropagation();
                                      togglePopup(loc?.location_id ?? '');
                                      navigate(
                                        '/dashboard/locations/edit/' +
                                          loc?.location_id
                                      );
                                    }}
                                    className="flex w-full flex-row items-center space-x-1 px-4 py-2 text-left align-middle text-primary hover:bg-gray-100"
                                  >
                                    <PencilSimple size={20} />
                                    <p>Edit</p>
                                  </button>
                                  <button
                                    onClick={(e: any) => {
                                      e.stopPropagation();
                                      togglePopup(loc?.location_id ?? '');
                                    }}
                                    className="flex w-full flex-row items-center space-x-1 px-4 py-2 text-left align-middle text-red-500 hover:bg-gray-100"
                                  >
                                    <Trash size={20} />
                                    <p>Delete</p>
                                  </button>
                                </div>
                              </div>
                            )}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <ClipLoader
                color="#1D213B"
                loading={loading}
                size={60}
                aria-label="Loading Spinner"
                data-testid="loader"
                className="flex h-full w-full items-center justify-center"
              />
            )}
          </div>
        );
    }
  };
  return <>{renderScreenView(screenView)}</>;
}

export default BusinessLocationsTable;
